/**
 * services/PreTradingSettingsService.ts
 * Pre-trading ritual configuration management service
 *
 * Provides CRUD operations for pre-trading settings that control the
 * morning preparation workflow, including personal prep fields, health
 * tracking, objectives, economic calendar, and transition screen.
 */

import { BaseService } from './BaseService';
import type { PreTradingSettings, QueuedObjective, TransitionQuote } from '@/types/schema/PreTradingSettings';
import type { DropdownOption } from '@/types/shared';
import { isEntityActive } from '@/types/shared/EntityStatus';
import { generateUUID } from '@/utils';
import { DEFAULT_TRANSITION_QUOTES } from '@/constants/preTrading';

/**
 * Result type for setQueuedObjectives operation
 */
interface SetQueuedObjectivesResult {
  settings: PreTradingSettings;
  warnings: string[];
}

/**
 * Result type for validateQueuedObjectives operation (validation only, no persist)
 */
interface ValidateQueuedObjectivesResult {
  warnings: string[];
}

/**
 * Service for managing pre-trading ritual configuration
 *
 * Extends BaseService to provide standard CRUD operations plus
 * specialized method for getting or creating default settings.
 */
export class PreTradingSettingsService extends BaseService<PreTradingSettings> {
  constructor() {
    super('pre_trading_settings');
  }

  /**
   * Get default pre-trading settings with sensible empty defaults
   *
   * @returns Default settings object (without id, createdAt, updatedAt)
   * @private
   */
  private getDefaultSettings(): Omit<PreTradingSettings, 'id' | 'createdAt' | 'updatedAt'> {
    return {
      // Personal Preparation - empty arrays ready for user customization
      personalPrepFields: [],
      healthMetrics: {
        source: 'custom',
        metrics: []
      },

      // Trading Objectives - conservative defaults
      maxObjectives: 3,
      pinObjectivesToLive: true,

      // NOTE: snapshotFields moved to MarketSnapshotSettings (v11)
      // NOTE: economicImpactAsset removed - economic calendar now uses EconomicCalendarSettings

      // Final Preparation
      finalCheckItems: [],

      // Transition Screen - disabled by default, user can enable
      enableTransitionScreen: false,
      transitionDuration: 5,
      // Clone default quotes to avoid shared references
      transitionQuotes: DEFAULT_TRANSITION_QUOTES.map(q => ({ ...q })),
      autoAdvance: false
    };
  }

  /**
   * Get existing settings or create default settings if none exist
   *
   * This is the primary method for retrieving pre-trading settings.
   * On first use, it creates a default settings record that the user
   * can then customize through the Settings GUI.
   *
   * @returns Promise resolving to PreTradingSettings
   */
  async getOrCreateDefaultSettings(): Promise<PreTradingSettings> {
    try {
      // Check if settings already exist
      const allSettings = await this.getAll();

      if (allSettings.length > 0) {
        // Return first settings record (safe access with non-null assertion after length check)
        const firstSetting = allSettings[0];
        if (!firstSetting) {
          throw new Error('Unexpected undefined settings record');
        }

        // Chain migrations: each operates on the result of the previous
        const afterHealthMigration = await this.migrateHealthMetricIds(firstSetting);
        const afterPrepFieldsMigration = await this.migratePersonalPrepFieldIds(afterHealthMigration);
        const afterQuotesMigration = await this.migrateTransitionQuotes(afterPrepFieldsMigration);
        return afterQuotesMigration;
      }

      // No settings exist, create default
      const defaultSettings = this.getDefaultSettings();
      const createdSettings = await this.add(defaultSettings);

      return createdSettings;
    } catch (error) {
      console.error('Failed to get or create pre-trading settings:', error);
      throw new Error('Failed to initialize pre-trading settings');
    }
  }

  /**
   * Migrate health metrics to include IDs if missing (v3 schema change)
   *
   * This migration adds stable IDs to health metrics for drag-and-drop reordering.
   * Only persists if a change was made to avoid unnecessary writes.
   *
   * @param settings - Current settings to check and migrate
   * @returns Settings with IDs added to all health metrics
   * @private
   */
  private async migrateHealthMetricIds(settings: PreTradingSettings): Promise<PreTradingSettings> {
    const metrics = settings.healthMetrics.metrics;

    // Check if any metric is missing an ID
    // Using type assertion since old data may not have id field
    const needsMigration = metrics.some(
      (m) => !(m as { id?: string }).id
    );

    if (!needsMigration) {
      return settings;
    }

    // Add IDs to metrics that don't have them
    const migratedMetrics = metrics.map((m) => {
      const metricWithOptionalId = m as { id?: string; name: string; min: number; max: number; unit?: string };
      if (metricWithOptionalId.id) {
        return m;
      }
      return {
        ...m,
        id: generateUUID()
      };
    });

    // Persist the migrated settings
    const updatedSettings: PreTradingSettings = {
      ...settings,
      healthMetrics: {
        ...settings.healthMetrics,
        metrics: migratedMetrics
      }
    };

    await this.update(settings.id, updatedSettings);

    return updatedSettings;
  }

  /**
   * Migrate personal prep fields to include IDs if missing (v4 schema change)
   *
   * This migration adds stable IDs to personal prep fields for drag-and-drop reordering.
   * Only persists if a change was made to avoid unnecessary writes.
   *
   * @param settings - Current settings to check and migrate
   * @returns Settings with IDs added to all personal prep fields
   * @private
   */
  private async migratePersonalPrepFieldIds(settings: PreTradingSettings): Promise<PreTradingSettings> {
    const fields = settings.personalPrepFields;

    // Check if any field is missing an ID
    // Using type assertion since old data may not have id field
    const needsMigration = fields.some(
      (f) => !(f as { id?: string }).id
    );

    if (!needsMigration) {
      return settings;
    }

    // Add IDs to fields that don't have them
    const migratedFields = fields.map((f) => {
      const fieldWithOptionalId = f as { id?: string; name: string; description?: string; type: 'checkbox' | 'dropdown' | 'text' | 'number'; options?: DropdownOption[] };
      if (fieldWithOptionalId.id) {
        return f;
      }
      return {
        ...f,
        id: generateUUID()
      };
    });

    // Persist the migrated settings
    const updatedSettings: PreTradingSettings = {
      ...settings,
      personalPrepFields: migratedFields
    };

    await this.update(settings.id, updatedSettings);

    return updatedSettings;
  }

  /**
   * Migrate transition quotes from string[] to TransitionQuote[] (v25 schema change)
   *
   * This migration handles three cases:
   * 1. Legacy string[] format → convert to TransitionQuote[]
   * 2. Partial objects (has text but missing id) → backfill id
   * 3. Already valid TransitionQuote[] → no migration needed
   *
   * Only persists if a change was made to avoid unnecessary writes.
   *
   * @param settings - Current settings to check and migrate
   * @returns Settings with quotes converted to TransitionQuote format
   * @private
   */
  private async migrateTransitionQuotes(settings: PreTradingSettings): Promise<PreTradingSettings> {
    // Defensive: handle missing or malformed transitionQuotes field
    const quotes = settings.transitionQuotes;
    if (!Array.isArray(quotes) || quotes.length === 0) {
      return settings;
    }

    // Check each quote and determine migration strategy
    // Using type assertion since old data may be string[] or partial objects
    const rawQuotes = quotes as unknown as Array<string | { id?: string; text?: string; author?: string }>;

    let needsMigration = false;
    const migratedQuotes: TransitionQuote[] = rawQuotes.map((quote) => {
      // Case 1: Legacy string format
      if (typeof quote === 'string') {
        needsMigration = true;
        return { id: generateUUID(), text: quote };
      }

      // Case 2: Object format - check if id is missing
      if (typeof quote === 'object' && quote !== null) {
        const text = quote.text ?? '';
        const author = quote.author;

        if (!quote.id) {
          // Missing id - backfill it
          needsMigration = true;
          return { id: generateUUID(), text, ...(author && { author }) };
        }

        // Case 3: Already valid - has both id and text
        return { id: quote.id, text, ...(author && { author }) };
      }

      // Fallback for unexpected data types - create empty quote with id
      needsMigration = true;
      return { id: generateUUID(), text: '' };
    });

    if (!needsMigration) {
      return settings;
    }

    // Persist the migrated settings
    const updatedSettings: PreTradingSettings = {
      ...settings,
      transitionQuotes: migratedQuotes
    };

    await this.update(settings.id, updatedSettings);

    return updatedSettings;
  }

  /**
   * Get current pre-trading settings
   *
   * Alias for getOrCreateDefaultSettings() for semantic clarity.
   *
   * @returns Promise resolving to current PreTradingSettings
   */
  async getCurrentSettings(): Promise<PreTradingSettings> {
    return this.getOrCreateDefaultSettings();
  }

  /**
   * Clear the queued objectives
   *
   * Resets queuedObjectives to an empty array.
   *
   * @returns Promise resolving to updated PreTradingSettings
   */
  async clearQueue(): Promise<PreTradingSettings> {
    const settings = await this.getOrCreateDefaultSettings();
    const updatedSettings = await this.update(settings.id, {
      ...settings,
      queuedObjectives: []
    });
    return updatedSettings;
  }

  /**
   * Validate queued objectives without persisting
   *
   * Validates queue constraints and generates warnings for non-active objectives.
   * Use this for pre-save validation when the caller will handle persistence.
   *
   * @param objectives - Array of objectives to validate
   * @returns Promise resolving to warnings array
   * @throws Error if more than one objective has isPrimary: true
   * @throws Error if duplicate objectiveId values are present
   */
  async validateQueuedObjectives(objectives: QueuedObjective[]): Promise<ValidateQueuedObjectivesResult> {
    // Validation phase (throws on constraint violations)
    const primaryCount = objectives.filter(obj => obj.isPrimary).length;
    if (primaryCount > 1) {
      throw new Error(`Queue validation failed: at most one objective can be primary, found ${primaryCount}`);
    }

    const objectiveIds = objectives.map(obj => obj.objectiveId);
    const uniqueIds = new Set(objectiveIds);
    if (uniqueIds.size !== objectiveIds.length) {
      const duplicates = objectiveIds.filter((id, index) => objectiveIds.indexOf(id) !== index);
      throw new Error(`Queue validation failed: duplicate objectiveId values found: ${[...new Set(duplicates)].join(', ')}`);
    }

    // Lookup phase (N+1 optimization)
    // Lazy import to avoid circular dependency
    const { tradingObjectiveService } = await import('./TradingObjectiveService');
    const allObjectives = await tradingObjectiveService.getAll();
    const objectiveMap = new Map(allObjectives.map(obj => [obj.id, obj]));

    // Warning generation
    const warnings: string[] = [];
    for (const queued of objectives) {
      const objective = objectiveMap.get(queued.objectiveId);
      if (!objective) {
        warnings.push(`Objective "${queued.objectiveId}" not found (may have been deleted)`);
      } else if (!isEntityActive(objective.status)) {
        warnings.push(`Objective "${objective.name}" has status "${objective.status}"`);
      }
    }

    return { warnings };
  }

  /**
   * Set the queued objectives for the next trading session
   *
   * Validates queue constraints and generates warnings for non-active objectives.
   *
   * @param objectives - Array of objectives to queue
   * @returns Promise resolving to settings and any warnings
   * @throws Error if more than one objective has isPrimary: true
   * @throws Error if duplicate objectiveId values are present
   */
  async setQueuedObjectives(objectives: QueuedObjective[]): Promise<SetQueuedObjectivesResult> {
    // Validate (throws on constraint violations, returns warnings)
    const { warnings } = await this.validateQueuedObjectives(objectives);

    // Persist phase
    const settings = await this.getOrCreateDefaultSettings();
    const updatedSettings = await this.update(settings.id, {
      ...settings,
      queuedObjectives: objectives
    });

    return { settings: updatedSettings, warnings };
  }
}

// Export singleton instance for use throughout the application
export const preTradingSettingsService = new PreTradingSettingsService();
